// src/components/home/MethodologyStrip.jsx
import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  Stack,
  HStack,
  VStack,
  Badge,
  Collapsible,
  Icon,
} from "@chakra-ui/react";
import { Info } from "lucide-react";
import { companies } from "../../../data/companies";

export default function MethodologyStrip({
  header = {
    title: "Our approach",
    subtitle: "What we evaluate when summarizing providers.",
  },
}) {
  const sub = { base: "gray.700", _dark: "gray.300" };
  const bg = { base: "white", _dark: "gray.800" };
  const border = { base: "gray.200", _dark: "whiteAlpha.300" };

  // Pull featured companies for examples
  const TAG = companies.find((c) => c.key === "TAG");
  const WYNN = companies.find((c) => c.key === "WYNN");

  const METRICS = [
    {
      key: "focus",
      title: "Focus & scope",
      color: "blue.400",
      summary:
        "We identify the types of cases each firm handles best—individual, small business, complex corporate, payroll/941, state issues, and audits.",
      bullets: [
        "Publicly stated specialties vs. actual case mix",
        "Depth across IRS programs (OIC, CNC, IA), audit & appeals",
        "State coordination and multi-year resolution experience",
      ],
      featuredNote:
        TAG && WYNN
          ? `In our reviews: ${TAG.name} is strong for ${(TAG.specialties || [])
              .slice(0, 2)
              .join(", ")} (${TAG.reviewCount} reviews, ${
              TAG.ratingAggregate
            }★), while ${WYNN.name} stands out for ${(WYNN.specialties || [])
              .slice(0, 2)
              .join(", ")} (${WYNN.reviewCount} reviews, ${
              WYNN.ratingAggregate
            }★).`
          : null,
    },
    {
      key: "transparency",
      title: "Transparency",
      color: "purple.400",
      summary:
        "We look for clear engagement terms, realistic expectations, and upfront discussion of fees and deliverables.",
      bullets: [
        "Written scopes: what’s included, what’s not",
        "Fee disclosure (fixed, phased, or hourly) and refund terms",
        "Plain-English explanations of risks and timelines",
      ],
      featuredNote:
        "We reward firms that publish meaningful FAQs, sample timelines, or phased pricing to set accurate expectations.",
    },
    {
      key: "track",
      title: "Track record & responsiveness",
      color: "green.400",
      summary:
        "Signals include tenure, complaint patterns, user-reported outcomes, and communication quality during intake and case work.",
      bullets: [
        "Years in operation and complaint velocity (BBB/AG where relevant)",
        "Client feedback on speed of first contact and ongoing updates",
        "Case progress markers: transcript pulls, filings, appeals",
      ],
      featuredNote:
        "Consistent, timely communication and clear next steps are weighted heavily—especially for time-sensitive IRS issues.",
    },
  ];

  return (
    <Box py={{ base: 10, md: 14 }}>
      <Container maxW="6xl">
        <Stack spacing={3} mb={6}>
          <Heading size="lg">{header.title}</Heading>
          {header.subtitle ? <Text color={sub}>{header.subtitle}</Text> : null}
        </Stack>

        {/* Distinct look: a single panel with collapsible rows */}
        <Box
          borderWidth="1px"
          borderColor={border}
          bg={bg}
          rounded="lg"
          overflow="hidden"
        >
          <Stack spacing={0}>
            {METRICS.map((m, idx) => (
              <Box
                key={m.key}
                _notFirst={{ borderTopWidth: "1px", borderColor: border }}
              >
                {/* Accent left bar */}
                <HStack align="stretch" spacing={0}>
                  <Box
                    w="6px"
                    bg={m.color}
                    opacity={0.9}
                    borderRightWidth="1px"
                    borderColor={border}
                  />
                  <Box flex="1" p={{ base: 4, md: 5 }}>
                    <Collapsible.Root lazyMount>
                      <Collapsible.Trigger
                        w="100%"
                        _hover={{
                          bg: { base: "gray.50", _dark: "whiteAlpha.100" },
                        }}
                        rounded="md"
                        px={2}
                        py={2}
                      >
                        <HStack justify="space-between" align="center">
                          <HStack spacing={3}>
                            <Badge
                              colorScheme={m.color.split(".")[0]}
                              variant="solid"
                            >
                              Metric
                            </Badge>
                            <Heading size="sm">{m.title}</Heading>
                          </HStack>
                          <Text fontSize="sm" color={sub}>
                            Details
                          </Text>
                        </HStack>
                      </Collapsible.Trigger>

                      <Collapsible.Content>
                        <VStack align="start" spacing={3} mt={3} px={2}>
                          <Text color={sub}>{m.summary}</Text>

                          {/* Bullets (safe ul/li to avoid import surprises) */}
                          <Box as="ul" pl={5} lineHeight="taller">
                            {m.bullets.map((b, i) => (
                              <Box as="li" key={i} color={sub}>
                                {b}
                              </Box>
                            ))}
                          </Box>

                          {m.featuredNote ? (
                            <HStack
                              align="start"
                              spacing={2}
                              p={3}
                              rounded="md"
                              borderWidth="1px"
                              borderColor={border}
                              bg={{ base: "gray.50", _dark: "whiteAlpha.100" }}
                            >
                              <Icon as={Info} />
                              <Text fontSize="sm" color={sub}>
                                {m.featuredNote}
                              </Text>
                            </HStack>
                          ) : null}
                        </VStack>
                      </Collapsible.Content>
                    </Collapsible.Root>
                  </Box>
                </HStack>
              </Box>
            ))}
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}
